import {ObjUtil} from 'yougile-util'
import {
  AllNotificationActions,
  ChatMessage,
  defaultNotificationSettings,
  disabledNotificationSettings,
  getMessageAssignIds,
  getNotificationSetting,
  Hub,
  isTaskNotificationAction,
  NotificationChannel,
  NotificationSettings,
  SystemMessageUtils,
  TaskNotificationAction,
  taskNotificationActions
} from 'yougile-model'
import {Sync} from '@client/sync'
import {Tracking, TrackingEvent} from '@common/tracking'
import {crmStore} from '@components/crm/crm-store'
import {globals} from '@extensions/globals-base'
import {Sticker} from './sticker/sticker'

export class NotificationSettingsService {
  static getDisabledSetting(
    action: AllNotificationActions,
    channel: NotificationChannel
  ): boolean {
    return !!disabledNotificationSettings[action]?.includes(channel)
  }

  static getTaskMessageFilter(
    settings: NotificationSettings,
    hub: Hub
  ): (m: ChatMessage) => boolean {
    const check = (action: TaskNotificationAction) =>
      getNotificationSetting(settings, action, 'chat')

    return (m: ChatMessage): boolean => {
      if (SystemMessageUtils.isSystemMessage(m)) {
        if (SystemMessageUtils.isKnownSystemMessage(m)) {
          if (
            SystemMessageUtils.isDeadlineNotice(m) ||
            SystemMessageUtils.isDeadline(m)
          ) {
            const assigned = Sticker.getUsers(hub)
            const hasSelf = assigned.includes(globals.currentUserId)
            const action = hasSelf ? 'deadline' : 'othersDeadline'
            return check(action)
          } else if (SystemMessageUtils.isAssignment(m)) {
            const assigned = getMessageAssignIds(m)
            const hasOthers =
              assigned.filter(id => id !== globals.currentUserId).length > 0
            const action = hasOthers ? 'otherAssigned' : 'selfAssigned'
            return check(action)
          } else if (SystemMessageUtils.isGtd(m)) {
            return check('gtd')
          } else if (SystemMessageUtils.isMove(m)) {
            if (crmStore.isDeal(hub)) {
              return false
            }
            return check('move')
          }
        }
        return true
      } else {
        return true
      }
    }
  }

  static getNotificationSettings(): NotificationSettings {
    const box = Sync.getProfile()
    const hub = box.getHub(box.companyId)
    return hub?.data.notificationSettings ?? defaultNotificationSettings
  }

  static setNotificationSettings(settings: NotificationSettings): void {
    const box = Sync.getProfile()
    const hub = box.getHub(box.companyId)

    // TODO! remove this block of code when fe will be released everywhere
    const settingsToSave: NotificationSettings = (
      Object.keys(settings) as Array<keyof NotificationSettings>
    ).reduce<NotificationSettings>((acc, settingKey) => {
      if (
        isTaskNotificationAction(settingKey) &&
        taskNotificationActions.includes(settingKey)
      ) {
        if (!acc.task) {
          acc.task = {}
        }

        acc.task[settingKey] = settings[settingKey]
      }
      acc[settingKey] = settings[settingKey]

      return acc
    }, {})

    if (hub) {
      hub.data.notificationSettings = settingsToSave
      hub.timestamp = Date.now()
      hub.commit()

      void Tracking.send(TrackingEvent.profileNotificationsSettingsChange)
    }
  }

  static migrateNotificationSettings(): void {
    const settings = NotificationSettingsService.getNotificationSettings()

    if (!ObjUtil.isEmpty(settings.task)) {
      NotificationSettingsService.setNotificationSettings({
        ...settings,
        ...settings.task
      })
    }
  }
}
